import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Divider,
  Flex,
  Heading,
  Icon,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import { CargoType, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { ConditionalField, confirmationOptions, prettyDate } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { HiOutlineExclamation } from 'react-icons/hi';
import { HiCalendarDays } from 'react-icons/hi2';

import { useDocuments } from '../../hooks/documents/useDocuments';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { API } from '../../utils/api-typings';
import { formatAwbNumber } from '../../utils/inputFormatters';
import { SimpleDatePickerInput } from '../DatePicker/SimpleDatePickerInput';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleInput } from '../SimpleInput';
import { SimpleSwitch } from '../SimpleSwitch';
import { SimpleTextarea } from '../SimpleTextarea';
import { ConditionalFields } from './ConditionalFieldsForm';

type Props = {
  quote: API.GetQuote;
  conditionalFields: ConditionalField[];
};

export const AirlineBookingForm = ({ quote, conditionalFields }: Props) => {
  const { control } = useFormContext();

  const requestId = quote?.request_id;
  const request = quote?.request;
  const expiresAt = quote?.expires_at;

  const { modalDisclosure } = useSelectedQuoteVariant();

  const [documentsInForm] = useDocuments({ requestId });
  const tenderDate = useWatch({ control, name: 'tender_date' });

  const isDangerousGoods = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);
  const shouldShowDgdWarning = useMemo(() => {
    const hasDocument = documentsInForm?.length > 0;

    if (isDangerousGoods && !hasDocument) return true;
    return false;
  }, [documentsInForm, isDangerousGoods]);

  const isExpired = useMemo(() => {
    if (!expiresAt) return false;

    const convertToTZ = (date: Date, timeZone: string) => {
      const newDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return newDate;
    };
    const originTimeZone = quote?.origin_airport?.time_zone;
    const adjustedExpiresAt = convertToTZ(expiresAt, originTimeZone);
    adjustedExpiresAt.setHours(23, 59, 59, 999);
    const now = convertToTZ(new Date(), originTimeZone);

    return adjustedExpiresAt < now;
  }, [expiresAt, quote?.origin_airport?.time_zone]);

  return (
    <Stack w="full" spacing="3">
      <Stack spacing="5">
        <Stack divider={<Divider />}>
          <Heading variant="h5">Airline Service</Heading>
          <Stack direction={{ base: 'column', sm: 'row' }} w="full">
            <SimpleInput
              label="AWB #"
              name="awb_number"
              placeholder="x x x | x x x x x x x x"
              maxLength={12}
              variant="outline"
              w="full"
              formatter={formatAwbNumber}
              isRequired={!!quote?.is_awb_required_to_book}
            />
            <Show if={!quote?.is_api_generated}>
              <SimpleDatePickerInput
                name="tender_date"
                label="Tender Date *"
                variant="outline"
                matchWidth={false}
                timeOfDay
                timeOfDayName="tender_date_time_of_day"
                iconFontSize="20px"
              />
            </Show>
            <Show if={quote?.is_api_generated}>
              <Stack
                minW="200px"
                borderWidth={1}
                p={4}
                borderRadius={6}
                borderColor="border.default"
                _hover={{ borderColor: 'border.hover' }}
                spacing={1}
                onClick={modalDisclosure.onOpen}
              >
                <Text textStyle="label" p={0} pb="1" pointerEvents="none">
                  Flight Departure Date
                </Text>
                <Stack direction="row" pointerEvents="none">
                  <Icon as={HiCalendarDays} fontSize={'20px'} />
                  <Text fontSize="md" color="text.default" w="full">
                    {prettyDate(tenderDate)}
                  </Text>
                </Stack>
              </Stack>
            </Show>
            <SimpleSwitch
              name="is_pre_screened"
              label="Pre-screened"
              options={confirmationOptions}
              flex="1"
              variant="outline"
            />
          </Stack>
        </Stack>

        <ConditionalFields quote={quote} fields={conditionalFields} />

        <Stack spacing="6">
          <Stack>
            <Heading variant="h5">Additional Information (Optional)</Heading>
            <SimpleInput
              label="Your Reference #"
              name="customer_ref"
              placeholder="x x x   x x x   x x x"
              variant="outline"
              flex="1"
            />
          </Stack>
          <SimpleTextarea
            name="additional_information"
            label="Additional Notes"
            placeholder="Additional notes"
            variant="outline"
            rows={2}
          />
        </Stack>
      </Stack>
      <Show if={shouldShowDgdWarning}>
        <Flex>
          <Tag size="lg" borderRadius="full" variant="outline" colorScheme="red" py="2">
            <TagLeftIcon as={HiOutlineExclamation} boxSize="6" />
            <TagLabel fontSize="lg">Missing Dangerous Goods Declaration - Upload to Book</TagLabel>
          </Tag>
        </Flex>
      </Show>
      <Show if={isDangerousGoods}>
        <SimpleDropzone documentType={DocumentType.DGD} requestId={requestId} />
      </Show>
      <Show if={isExpired}>
        <Flex>
          <Alert status="error" variant="subtle" fontSize="sm" borderRadius="md">
            <AlertIcon />
            <Stack spacing="1">
              <AlertTitle>Quote Expired</AlertTitle>
              <AlertDescription>
                This quote has passed its expiry date. Booking this may not result in the same price.
              </AlertDescription>
            </Stack>
          </Alert>
        </Flex>
      </Show>
    </Stack>
  );
};
